import type { KeyboardEvent } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { TapTooltip } from '../../../../components/touch/TapTooltip';
import {
    formatSpan,
    lifetimeParts,
    lifetimeProgress,
    lifetimeText,
    lifetimeWindow,
    type Lifetime,
} from '../lifetime';

interface LifetimeBarProps {
    lifetime: Lifetime;
    shown: readonly [number, number] | null;
    left: number;
    right: number;
    bottom: number;
    /** How far under the box's bottom edge the time axis lies, when what the box holds is taller than the box. */
    sink?: number;
}

const BAR_HEIGHT = 3;
const HIT_HEIGHT = 11;
const MARK_HEIGHT = 9;

// What a native button does with the keys: Enter clicks on the key down,
// Space on the key up (and must not scroll the page).
function pressedLikeAButton(event: KeyboardEvent<HTMLElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.click();
    } else if (event.key === ' ') {
        event.preventDefault();
    }
}

function releasedLikeAButton(event: KeyboardEvent<HTMLElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === ' ') event.currentTarget.click();
}

function day(ms: number): string {
    return new Date(ms).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function TooltipBody({ lifetime }: { lifetime: Lifetime }) {
    const rows: [string, string][] = [
        ['Start', day(lifetime.start)],
        ['End', day(lifetime.end)],
    ];
    if (lifetime.state === 'open') {
        rows.push(['Done', `${Math.round(lifetimeProgress(lifetime) * 100)}%`]);
        rows.push(['Left', formatSpan(lifetime.end - lifetime.now)]);
    }
    if (lifetime.state === 'overdue') {
        rows.push(['Overdue', formatSpan(lifetime.now - lifetime.end)]);
    }
    return (
        <Box sx={{ py: 0.25 }}>
            {rows.map(([name, value]) => (
                <Box
                    key={name}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Typography sx={{ fontSize: '11px', opacity: 0.7 }}>
                        {name}
                    </Typography>
                    <Typography
                        sx={{ fontSize: '11px', fontFamily: 'monospace' }}
                    >
                        {value}
                    </Typography>
                </Box>
            ))}
            {lifetime.state === 'overdue' && (
                <Typography sx={{ fontSize: '11px', mt: 0.5, maxWidth: 200 }}>
                    The end date is past. The market is still open.
                </Typography>
            )}
            {lifetime.state === 'closed' && (
                <Typography sx={{ fontSize: '11px', mt: 0.5 }}>
                    The market is closed.
                </Typography>
            )}
        </Box>
    );
}

export function LifetimeBar({
    lifetime,
    shown,
    left,
    right,
    bottom,
    sink = 0,
}: LifetimeBarProps) {
    const theme = useTheme();
    const ink = theme.palette.text.primary;
    const parts = lifetimeParts(lifetime);
    const closed = lifetime.state === 'closed';
    const overdue = lifetime.state === 'overdue';
    const window = shown ? lifetimeWindow(lifetime, shown) : null;
    const pct = (share: number) => `${share * 100}%`;
    const elapsedEnd = parts.overdueFrom ?? parts.elapsed;

    return (
        <>
            <TapTooltip
                title={<TooltipBody lifetime={lifetime} />}
                placement="top"
            >
                <Box
                    data-testid="lifetime-bar"
                    data-state={lifetime.state}
                    // A TapTooltip trigger is a control: focusable, named,
                    // and Enter or Space opens the tooltip (a div sends no
                    // click for them, so the keys click it).
                    role="button"
                    tabIndex={0}
                    aria-label={`Market lifetime, ${lifetimeText(lifetime)}`}
                    onKeyDown={pressedLikeAButton}
                    onKeyUp={releasedLikeAButton}
                    sx={{
                        position: 'absolute',
                        left,
                        right,
                        bottom: bottom - sink - (HIT_HEIGHT - BAR_HEIGHT) / 2,
                        height: HIT_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 1,
                        borderRadius: 1,
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: 2,
                        },
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: BAR_HEIGHT,
                            borderRadius: BAR_HEIGHT,
                            bgcolor: alpha(ink, 0.1),
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: pct(elapsedEnd),
                                borderRadius: BAR_HEIGHT,
                                bgcolor: alpha(ink, closed ? 0.22 : 0.34),
                            }}
                        />
                        {window && (
                            <Box
                                data-testid="lifetime-window"
                                sx={{
                                    position: 'absolute',
                                    left: pct(window[0]),
                                    width: pct(window[1] - window[0]),
                                    top: 0,
                                    bottom: 0,
                                    bgcolor: alpha(ink, 0.6),
                                }}
                            />
                        )}
                        {parts.overdueFrom !== null && (
                            <Box
                                data-testid="lifetime-overdue"
                                sx={{
                                    position: 'absolute',
                                    left: pct(parts.overdueFrom),
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    borderRadius: BAR_HEIGHT,
                                    bgcolor: theme.palette.warning.main,
                                }}
                            />
                        )}
                        {window &&
                            window.map((share, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        position: 'absolute',
                                        left: `calc(${pct(share)} - 1px)`,
                                        top: (BAR_HEIGHT - MARK_HEIGHT) / 2,
                                        width: 2,
                                        height: MARK_HEIGHT,
                                        borderRadius: 1,
                                        bgcolor: ink,
                                    }}
                                />
                            ))}
                    </Box>
                </Box>
            </TapTooltip>
            <Typography
                data-testid="lifetime-text"
                sx={{
                    position: 'absolute',
                    right: 0,
                    bottom: 2 - sink,
                    px: 0.5,
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    lineHeight: '14px',
                    whiteSpace: 'nowrap',
                    bgcolor: 'background.paper',
                    color: overdue ? 'warning.main' : 'text.secondary',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}
            >
                {lifetimeText(lifetime)}
            </Typography>
        </>
    );
}
